import Application from 'types/application';
import { ApplicationUsageStatistics } from 'types/application_usage_statistics';
import { OrganizationUsageLimits } from 'types/organization_usage_limits';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQueries } from 'hooks/api/useAuthenticatedQuery';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useOrganizationApplications } from 'hooks/api/useApplications';

export type WorkspaceUtilization = {
  application: Application;
  /** `undefined` while loading or when the request failed. */
  limits: OrganizationUsageLimits | null | undefined;
  /** `undefined` while loading or when the request failed. */
  statistics: ApplicationUsageStatistics | undefined;
};

/**
 * Usage limits and usage statistics for every workspace of the
 * current organization.
 */
export function useWorkspacesUtilization() {
  const { client } = useAPI();
  const { data: organization } = useCurrentOrganization();
  const { data: applications, isInitialLoading: isLoadingApplications } =
    useOrganizationApplications(organization?.id);

  const limitQueries = useAuthenticatedQueries(
    (applications ?? []).map((application) => ({
      // Shared with `useApplicationUsageLimits` so both views stay in sync.
      queryKey: ['application-usage-limits', application.id],
      async queryFn() {
        const { data } = await client.get<OrganizationUsageLimits | null>(
          `/internal/apps/${application.id}/usage_limits`
        );
        return data;
      },
    }))
  );

  const statisticsQueries = useAuthenticatedQueries(
    (applications ?? []).map((application) => ({
      // Shared with `useApplicationUsageStatistics`.
      queryKey: ['application-usage-statistics', application.id],
      async queryFn() {
        const { data } = await client.get<ApplicationUsageStatistics>(
          `/internal/apps/${application.id}/usage_statistics`
        );
        return data;
      },
    }))
  );

  const workspaces: WorkspaceUtilization[] = (applications ?? []).map(
    (application, index) => ({
      application,
      limits: limitQueries[index]?.data as
        | OrganizationUsageLimits
        | null
        | undefined,
      statistics: statisticsQueries[index]?.data as
        | ApplicationUsageStatistics
        | undefined,
    })
  );

  return {
    workspaces,
    isLoading:
      isLoadingApplications ||
      [...limitQueries, ...statisticsQueries].some(
        (query) => query.isInitialLoading
      ),
  };
}
